
import React, { useState, useEffect } from 'react';
import BrandFooter from './BrandFooter';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  showNav?: boolean;
  onLogout?: () => void;
  userName?: string;
  department?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, title, showNav, onLogout, userName, department }) => {
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' ||
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
      {/* Mobile Portrait Restriction Overlay */}


      {showNav ? (
        <header className="bg-white dark:bg-gray-900 border-b dark:border-gray-800 sticky top-0 z-50 transition-colors">
          <div className="w-full px-4 sm:px-10 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/maghil-hub-logo.png" className="h-12 w-auto object-contain transition-all" alt="Logo" />
            </div>
            <div className="flex items-center gap-3">
              {userName && (
                <div className="hidden sm:flex items-center gap-3 px-5 h-10 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 transition-colors">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0"></div>
                  <div className="flex flex-col items-start leading-tight">
                    <span className="text-[11px] font-black text-gray-900 dark:text-white uppercase tracking-wider">{userName}</span>
                    {department && <span className="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">{department}</span>}
                  </div>
                </div>
              )}
              <button
                onClick={toggleTheme}
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all shadow-sm border border-gray-100 dark:border-gray-700"
                title="Toggle Dark Mode"
              >
                {isDark ? '☀️' : '🌙'}
              </button>
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="text-sm font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-1.5 rounded-md transition"
                >
                  Logout
                </button>
              )}
            </div>
          </div>
        </header>
      ) : (
        <div className="fixed top-6 right-6 z-[100] flex items-center gap-3">
          {userName && (
            <div className="hidden md:flex items-center gap-4 px-6 h-14 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 transition-all">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse flex-shrink-0"></div>
              <div className="flex flex-col items-start leading-tight">
                <span className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-[0.1em]">{userName}</span>
                {department && <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{department}</span>}
              </div>
            </div>
          )}
          <button
            onClick={toggleTheme}
            className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-xl border border-gray-100 dark:border-gray-700 hover:scale-105 active:scale-95 transition-all text-2xl"
            title="Toggle Dark Mode"
          >
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
      )}
      <main className="flex-grow pb-16">
        {children}
      </main>
      <BrandFooter />
    </div>
  );
};

export default Layout;
